import { useEffect } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import livelinxAuthLogo from '@/assets/images/livelinx-auth-logo.png';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ImageWithLoader } from '@/components/ui/image-with-loader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthSession } from '@/hooks/queries';
import { PolicyModal } from '@/modules/policy-modal';

import authConfig from '../config/auth-config.json';
import { useAuth } from '../hooks/useAuth';
import { AuthFormProps } from '../types';

/**
 * Authentication Form Component
 *
 * SIGNUP DISABLED BY DESIGN - Only login is available.
 * Contact admin for new account creation.
 *
 * Features:
 * - Email/password authentication
 * - Auto-redirect after successful login
 * - Branding customization via config
 */
export const AuthForm = ({ config = authConfig }: AuthFormProps) => {
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const { email, setEmail, password, setPassword, loading, handleAuth } = useAuth(config);

  const { data: session, isLoading: sessionLoading } = useAuthSession();

  const handleCheckboxClick = () => {
    if (!termsAccepted) {
      setShowPolicyModal(true);
    } else {
      setTermsAccepted(false);
    }
  };

  const handlePolicyAccept = () => {
    setTermsAccepted(true);
    setShowPolicyModal(false);
  };

  useEffect(() => {
    if (session && !sessionLoading) {
      navigate(config.navigation.redirectAfterAuth);
    }
  }, [session, sessionLoading, navigate, config.navigation.redirectAfterAuth]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-livelinx-grey100 p-4">
      <Card className="w-full max-w-md bg-white p-8 shadow-white">
        <div className="mb-8 text-center">
          {config.branding.showLogo && (
            <div className="mb-6 flex justify-center">
              <ImageWithLoader
                src={livelinxAuthLogo}
                alt={config.branding.logoAlt}
                className="h-auto w-32 object-contain"
                containerClassName="flex justify-center w-32"
                skeletonClassName="rounded-md h-20"
                loading="eager"
                fetchPriority="high"
                width={128}
                height={103}
                onClick={undefined}
                linkTo="https://www.livelinx.com"
              />
            </div>
          )}
          <h1 className="mb-2 font-heading text-4xl text-livelinx-purple200">
            {config.form.title}
          </h1>
          <p className="text-livelinx-grey200">{config.form.loginSubtitle}</p>
        </div>

        <form onSubmit={(e) => void handleAuth(e)} className="space-y-4">
          <div>
            <Label htmlFor="identifier" className="text-livelinx-text">
              {config.form.fields.identifier.label}
            </Label>
            <Input
              id="identifier"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={config.form.fields.identifier.placeholder}
              className="mt-1 border-livelinx-lightgray100"
              autoComplete="username"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-livelinx-text">
              {config.form.fields.password.label}
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={config.form.fields.password.minLength}
              placeholder={config.form.fields.password.placeholder}
              className="mt-1 border-livelinx-lightgray100"
            />
          </div>

          {/* Terms and conditions checkbox */}
          <div className="flex items-start space-x-3 pt-2">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={handleCheckboxClick}
              className="mt-1"
            />
            <label
              htmlFor="terms"
              className="cursor-pointer select-none text-sm leading-relaxed text-livelinx-text"
              onClick={(e) => {
                e.preventDefault();
                handleCheckboxClick();
              }}
            >
              {config.form.termsCheckbox.label}
            </label>
          </div>

          <Button
            type="submit"
            disabled={loading || !termsAccepted}
            className="w-full bg-primary hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? config.form.buttons.loading : config.form.buttons.login}
          </Button>
        </form>
      </Card>

      {/* Policy Modal */}
      <PolicyModal isOpen={showPolicyModal} onClose={() => {}} onAccept={handlePolicyAccept} />
    </div>
  );
};
